import React, { Suspense } from 'react'
import { BiCog } from 'react-icons/bi'
import ReactGA from 'react-ga4'
import { Redirect, Route, RouteComponentProps, RouteProps, Switch, useLocation } from 'react-router'
import { BrowserRouter as Router } from 'react-router-dom'
// Imported from their own modules rather than through `components/index.ts`:
// that barrel re-exports every component in the app, and pulling it into an
// eagerly-loaded module dragged all of them — tables, charts, the video player,
// the tour guide — into the initial bundle, which is what made the lazy route
// chunks come out nearly empty.
import FillLoader from '../components/UI/Loaders/FillLoader'
import SideBar from '../components/layouts/SideBar'
import PageNotFound from '../containers/PageNotFound'
import { useAuthContext } from '../context/AuthProvider'

import { PRIVATE_ROUTES, PrivateRouteObject, PUBLIC_ROUTES } from './routes'

import { DATA_COLLECTOR, GA_MEASURING_ID } from '../constants'
import ErrorBoundary from '../layouts/ErrorBoundary'
import { theme } from 'theme'
import useSocketConnector from '../hooks/useSocketConnector'
import usePostRegistrationGate from '../hooks/usePostRegistrationGate'
import { FiMap, FiSmartphone } from 'react-icons/fi'
import {
  FaClipboardCheck,
  FaClipboardList,
  FaList,
  FaProjectDiagram,
  FaTags,
  FaTasks
} from 'react-icons/fa'
import { VscQuestion, VscSettings } from 'react-icons/vsc'
import { MdDashboard, MdGroup } from 'react-icons/md'
import { Activity, BarChart2 } from 'react-feather'
import { FiBell } from 'react-icons/fi'

const ICON_COLOR = theme.colors.gray[500]
const ICON_SIZE = 16
interface RouteType extends RouteProps {
  component: any
}

type NavItem = {
  to: string
  title: string
  icon: React.ReactNode
  className?: string
}

type NavCategory = {
  items: NavItem[]
  icon: React.ReactNode
}

export type NavItems = Record<string, NavCategory>

function buildNavItems(): NavItems {
  const dashboardItems: NavItem[] = [
    {
      to: `/auth/insights`,
      title: 'Team Impact',
      icon: <Activity size={ICON_SIZE} color={ICON_COLOR} className="sidebar-menu-icon" />
    },
    {
      to: `/auth/insight-analytics`,
      title: 'Analytics',
      icon: <BarChart2 size={ICON_SIZE} color={ICON_COLOR} className="sidebar-menu-icon" />
    },
    {
      to: `/auth/map`,
      title: 'Map View',
      icon: <FiMap size={ICON_SIZE} color={ICON_COLOR} className="sidebar-menu-icon" />
    },
    {
      to: `/auth/tasks`,
      title: 'Task Feed',
      icon: <FaTasks size={ICON_SIZE} color={ICON_COLOR} className="sidebar-menu-icon" />
    }
  ]

  return {
    dashboard: {
      items: dashboardItems,
      icon: <MdDashboard size={ICON_SIZE} color={ICON_COLOR} className="sidebar-menu-icon" />
    },
    create: {
      items: [
        {
          to: `/auth/task-types-list`,
          title: 'Template Builder',
          icon: (
            <FaClipboardCheck size={ICON_SIZE} color={ICON_COLOR} className="sidebar-menu-icon" />
          ),
          className: 'task-builder-menu-option'
        },
        {
          to: `/auth/task-types/create-with-templates`,
          title: 'Templates',
          icon: (
            <FaClipboardList size={ICON_SIZE} color={ICON_COLOR} className="sidebar-menu-icon" />
          ),
          className: 'task-builder-menu-option'
        },
        {
          to: `/auth/task-types-list/create-task?view=tags`,
          title: 'Tags',
          icon: <FaTags size={ICON_SIZE} color={ICON_COLOR} className="sidebar-menu-icon" />,
          className: 'task-builder-menu-option'
        },
        {
          to: `/auth/task-types-list?view=data-lists`,
          title: 'Data Lists',
          icon: <FaList size={ICON_SIZE} color={ICON_COLOR} className="sidebar-menu-icon" />,
          className: 'task-builder-menu-option'
        }
      ],
      icon: <FaClipboardCheck size={ICON_SIZE} color={ICON_COLOR} className="sidebar-menu-icon" />
    },
    manage: {
      items: [
        {
          to: `/auth/projects`,
          title: 'Projects',
          icon: (
            <FaProjectDiagram size={ICON_SIZE} color={ICON_COLOR} className="sidebar-menu-icon" />
          ),
          className: 'projects-menu-option'
        },
        {
          to: `/auth/communities`,
          title: 'Teams',
          icon: <MdGroup size={ICON_SIZE} color={ICON_COLOR} className="sidebar-menu-icon" />,
          className: 'groups-menu-option'
        },
        {
          to: `/auth/baoriders`,
          title: DATA_COLLECTOR + 's',
          icon: <FiSmartphone size={ICON_SIZE} color={ICON_COLOR} className="sidebar-menu-icon" />,
          className: 'data-collectors-menu-option'
        },
        {
          to: `/auth/notifications`,
          title: 'Notifications',
          icon: <FiBell size={ICON_SIZE} color={ICON_COLOR} className="sidebar-menu-icon" />,
          className: 'notifications-menu-option'
        },
        {
          to: `/auth/settings`,
          title: 'Settings',
          icon: <VscSettings size={ICON_SIZE} color={ICON_COLOR} className="sidebar-menu-icon" />,
          className: 'settings-menu-option'
        }
      ],
      icon: <BiCog size={ICON_SIZE} color={ICON_COLOR} className="sidebar-menu-icon" />
    },
    help: {
      items: [
        {
          to: `/auth/help`,
          title: 'Help Center',
          icon: <VscQuestion size={ICON_SIZE} color={ICON_COLOR} className="sidebar-menu-icon" />
        }
      ],
      icon: <VscQuestion size={ICON_SIZE} color={ICON_COLOR} className="sidebar-menu-icon" />
    }
  }
}

/**
 * Decide once whether the signed-in customer may see authenticated routes.
 *
 * This used to live inside `PrivateRoute`, which is rendered once per route —
 * 28 of them, all mounted together inside the sidebar. That meant 28 copies of
 * `useMeQuery` + `useIsSubscribed` + the feature-access query being watched on
 * every authenticated screen, for a decision that is identical for all of them.
 *
 * @returns a redirect/loader element to render instead of the route, or null
 *          when the route may render normally.
 */
const useAuthGateOutcome = (location?: RouteComponentProps['location']) => {
  const { isAuthenticating, isAuthenticated } = useAuthContext()
  const { loading: gateLoading, needsSetup } = usePostRegistrationGate()

  if (isAuthenticating) {
    return <FillLoader />
  }

  if (!isAuthenticated) {
    return <Redirect to={{ pathname: '/', state: { from: location } }} />
  }

  // Hold until the user's setup state is known, so we neither flash a page
  // the customer shouldn't see nor redirect them prematurely.
  if (gateLoading) {
    return <FillLoader color="black" />
  }

  // A guided-onboarding customer who hasn't finished the pay gate must
  // complete it before using the platform — send them to the pay gate
  // (the registration wizard resumes there). `/register` is a public route,
  // so this can never loop.
  if (needsSetup) {
    return <Redirect to={{ pathname: '/register' }} />
  }

  return null
}

/**
 * Renders the authenticated routes underneath a single gate evaluation.
 * `Switch` keeps React Router from walking all 28 `Route`s on every render.
 */
const PrivateRoutes = ({ routes }: { routes: PrivateRouteObject[] }) => {
  const location = useLocation()
  const blocked = useAuthGateOutcome(location)

  if (blocked) return blocked

  return (
    <Suspense fallback={<FillLoader color="black" />}>
      <Switch>
        {routes.map(({ component: Component, ...route }) => (
          <Route key={route.path} {...route} render={() => <Component {...route} />} />
        ))}
        <Route render={PageNotFound} />
      </Switch>
    </Suspense>
  )
}

/** A single authenticated route rendered outside the sidebar chrome. */
const PrivateRoute = ({ component: Component, ...rest }: RouteType) => {
  const location = useLocation()
  const blocked = useAuthGateOutcome(location)

  return (
    <Route
      {...rest}
      render={() =>
        blocked || (
          <Suspense fallback={<FillLoader color="black" />}>
            <Component {...rest} />
          </Suspense>
        )
      }
    />
  )
}

const PublicRoute = ({ component: Component, ...rest }: RouteType) => (
  <Route {...rest}>
    <Component />
  </Route>
)

// Route lists are static; computing them per render was pure waste.
const WRAPPED_PRIVATE_ROUTES = PRIVATE_ROUTES.filter((route) => !route.hidePageWrap)
const UNWRAPPED_PRIVATE_ROUTES = PRIVATE_ROUTES.filter((route) => route.hidePageWrap)

const Navigation = () => {
  const navItems = React.useMemo(() => buildNavItems(), [])

  React.useEffect(() => {
    // `initialize` used to run on every render of this component, re-creating
    // the GA tracker each time.
    ReactGA.initialize(GA_MEASURING_ID)
    ReactGA.send('pageview')
  }, [])

  useSocketConnector()

  return (
    <ErrorBoundary>
      <Router>
        <Suspense fallback={<FillLoader />}>
          <Switch>
            {PUBLIC_ROUTES.map((route) => {
              return <PublicRoute key={route.path} {...route} />
            })}

            {/* Private routes without the admin wrapper */}
            {UNWRAPPED_PRIVATE_ROUTES.map((route) => {
              return <PrivateRoute key={route.path} {...route} />
            })}

            {/* Normal auth routes with admin wrapper */}
            <Route
              path="/auth"
              render={() => {
                return (
                  <SideBar
                    bg="gray.50"
                    color={ICON_COLOR}
                    navItems={navItems}
                    hoverColor={theme.colors.gray[100]}
                    accentColor={theme.colors.gray[900]}
                    tooltipBg={theme.colors.gray[900]}
                    tooltipColor={theme.colors.gray[50]}
                  >
                    <PrivateRoutes routes={WRAPPED_PRIVATE_ROUTES} />
                  </SideBar>
                )
              }}
            />
            <Route path="/terms-and-conditions.pdf" />
            <Route render={PageNotFound} />
          </Switch>
        </Suspense>
      </Router>
    </ErrorBoundary>
  )
}

export default Navigation
